import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AdminTable } from "components/shared/AdminTable";
import { AdminTablePagination } from "components/shared/AdminTablePagination";
import { usePropertyGuests } from "./hooks/usePropertyData";
import { Badge } from "components/ui/badge";
import { SortableTableHead } from "components/shared/SortableTableHead";

const DEFAULT_PAGE_SIZE = 10;

type SortField = "name" | "email" | "keyActivated" | "physicalKeys";
type SortDirection = "asc" | "desc";

const getFullName = (firstName?: string | null, lastName?: string | null) => {
  const parts = [firstName, lastName].filter(Boolean);
  return parts.length > 0 ? parts.join(" ") : "-";
};

export default function PropertyGuests() {
  const { externalId } = useParams<{ externalId: string }>();
  const navigate = useNavigate();
  const { data, isLoading, isError } = usePropertyGuests(externalId!);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState<SortField>("name");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
    setPage(0);
  };

  // Filter, sort, and paginate data
  const filteredAndSortedData = useMemo(() => {
    if (!data) return [];

    // Fuzzy search filter
    let filtered = data;
    if (search.trim()) {
      const searchLower = search.toLowerCase();
      filtered = data.filter((guest) => {
        const name = getFullName(guest.firstName, guest.lastName).toLowerCase();
        const email = (guest.email ?? "").toLowerCase();
        const id = (guest.id ?? "").toLowerCase();
        return (
          name.includes(searchLower) ||
          email.includes(searchLower) ||
          id.includes(searchLower)
        );
      });
    }

    // Sort
    const sorted = [...filtered].sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case "name":
          comparison = getFullName(a.firstName, a.lastName).localeCompare(
            getFullName(b.firstName, b.lastName)
          );
          break;
        case "email":
          comparison = (a.email ?? "").localeCompare(b.email ?? "");
          break;
        case "keyActivated":
          comparison = Number(a.hasActivatedKey) - Number(b.hasActivatedKey);
          break;
        case "physicalKeys":
          comparison = (a.physicalKeys?.length ?? 0) - (b.physicalKeys?.length ?? 0);
          break;
      }
      return sortDirection === "asc" ? comparison : -comparison;
    });

    return sorted;
  }, [data, search, sortField, sortDirection]);

  const paginatedData = useMemo(() => {
    const start = page * pageSize;
    const end = start + pageSize;
    return filteredAndSortedData.slice(start, end);
  }, [filteredAndSortedData, page, pageSize]);

  const totalPages = useMemo(() => {
    return Math.ceil(filteredAndSortedData.length / pageSize);
  }, [filteredAndSortedData, pageSize]);

  return (
    <AdminTable
      aria-label="Property guests"
      toolbar={
        <div className="px-4 py-4">
          <label className="grid w-full gap-1 text-xs font-semibold tracking-wide uppercase text-slate-500 sm:max-w-sm">
            Search guests
            <input
              className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm font-normal normal-case text-slate-800 outline-none transition-colors placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(0);
              }}
              placeholder="Search by name, email, or ID"
            />
          </label>
        </div>
      }
      footer={
        !isLoading && !isError && totalPages > 1 ? (
          <AdminTablePagination
            currentPage={page}
            pageSize={pageSize}
            visibleRowCount={paginatedData.length}
            disableNext={page >= totalPages - 1}
            onNext={() => setPage((currentPage) => currentPage + 1)}
            onPrevious={() => setPage((currentPage) => Math.max(0, currentPage - 1))}
            onPageSizeChange={(newPageSize) => {
              setPageSize(newPageSize);
              setPage(0);
            }}
          />
        ) : undefined
      }
    >
      <thead>
        <tr>
          <SortableTableHead
            active={sortField === "name"}
            ariaLabel="Sort by name"
            direction={sortField === "name" ? sortDirection : "asc"}
            onSort={() => handleSort("name")}
            scope="col"
          >
            Name
          </SortableTableHead>
          <SortableTableHead
            active={sortField === "email"}
            ariaLabel="Sort by email"
            direction={sortField === "email" ? sortDirection : "asc"}
            onSort={() => handleSort("email")}
            scope="col"
          >
            Email
          </SortableTableHead>
          <SortableTableHead
            active={sortField === "keyActivated"}
            ariaLabel="Sort by key activation"
            direction={sortField === "keyActivated" ? sortDirection : "asc"}
            onSort={() => handleSort("keyActivated")}
            scope="col"
          >
            Key Activated
          </SortableTableHead>
          <SortableTableHead
            active={sortField === "physicalKeys"}
            ariaLabel="Sort by physical key count"
            direction={sortField === "physicalKeys" ? sortDirection : "asc"}
            onSort={() => handleSort("physicalKeys")}
            scope="col"
          >
            Physical Keys
          </SortableTableHead>
        </tr>
      </thead>
      <tbody>
        {isLoading ? (
          <tr>
            <td colSpan={4}>
              <div className="flex items-center justify-center min-h-80 text-sm text-slate-500">
                Loading guests...
              </div>
            </td>
          </tr>
        ) : isError ? (
          <tr>
            <td colSpan={4}>
              <div className="flex items-center justify-center px-4 text-sm text-center min-h-80 text-rose-700">
                Error loading guests.
              </div>
            </td>
          </tr>
        ) : !data || data.length === 0 ? (
          <tr>
            <td colSpan={4} className="py-16 text-sm text-center text-slate-500">
              No guests found for this property.
            </td>
          </tr>
        ) : filteredAndSortedData.length === 0 ? (
          <tr>
            <td colSpan={4} className="py-16 text-sm text-center text-slate-500">
              No guests match your search.
            </td>
          </tr>
        ) : (
          paginatedData.map((guest) => (
            <tr
              className="cursor-pointer"
              key={guest.id}
              onClick={() => navigate(`/admin/guests/${guest.id}`)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  navigate(`/admin/guests/${guest.id}`);
                }
              }}
              tabIndex={0}
            >
              <td>
                <div>{getFullName(guest.firstName, guest.lastName)}</div>
                <div className="pt-1 font-mono text-xs text-slate-500">
                  {guest.id}
                </div>
              </td>
              <td>{guest.email ?? "-"}</td>
              <td>
                <Badge variant={guest.hasActivatedKey ? "success" : "secondary"}>
                  {guest.hasActivatedKey ? "Yes" : "No"}
                </Badge>
              </td>
              <td>{guest.physicalKeys?.length ?? 0}</td>
            </tr>
          ))
        )}
      </tbody>
    </AdminTable>
  );
}
